import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightning, X, ArrowRight, CheckCircle, Play } from '@phosphor-icons/react';

export default function ActivationPanel({ activationData, onDismiss }) {
  const navigate = useNavigate();

  if (!activationData) return null;

  const { improvements = [], suggested_systems = [], provider_name } = activationData;
  if (improvements.length === 0) return null;

  // Filter to only actionable suggestions (have a CTA)
  const actionable = suggested_systems.filter(s => s.cta);
  const active = suggested_systems.filter(s => s.state === 'active');

  return (
    <div
      className="fixed bottom-6 right-6 z-50 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="activation-panel"
    >
      <div className="bg-[#1E2836] border border-[rgba(0,229,192,0.15)] rounded-2xl shadow-2xl shadow-[rgba(0,229,192,0.05)] overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[rgba(0,229,192,0.1)] flex items-center justify-center">
                <Lightning size={12} className="text-[#00E5C0]" weight="fill" />
              </div>
              <span className="text-[13px] font-semibold text-[#F0F4F8]" data-testid="activation-panel-title">
                Your AI just improved
              </span>
            </div>
            <button
              onClick={onDismiss}
              className="text-[#3A4150] hover:text-[#7D8896] transition-colors p-0.5"
              data-testid="activation-panel-dismiss"
            >
              <X size={12} />
            </button>
          </div>

          <div className="space-y-1.5 ml-8">
            {improvements.slice(0, 3).map((imp, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`activation-improvement-${i}`}>
                <ArrowRight size={9} className="text-[#00E5C0] flex-shrink-0" />
                <span className="text-[12px] text-[#B0B8C4]">{imp}</span>
              </div>
            ))}
          </div>
          {provider_name && (
            <div className="mt-2 ml-8">
              <span className="text-[10px] text-[#3A4150]">via {provider_name}</span>
            </div>
          )}
        </div>

        {/* Suggested systems — outcome-led with state awareness */}
        {actionable.length > 0 && (
          <div className="border-t border-[rgba(255,255,255,0.08)] px-4 py-3">
            <p className="text-[10px] font-semibold text-[#5C6778] uppercase tracking-wider mb-2" data-testid="activation-systems-title">
              You can now run these systems
            </p>
            <div className="space-y-1.5">
              {actionable.slice(0, 4).map((sys) => (
                <button
                  key={sys.template_id}
                  onClick={() => { onDismiss(); navigate('/systems'); }}
                  className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(0,229,192,0.04)] border border-[rgba(255,255,255,0.04)] hover:border-[rgba(0,229,192,0.1)] transition-all group text-left"
                  data-testid={`activation-system-${sys.template_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <span className="text-[12px] text-[#C8D1DB] font-medium block leading-tight">
                      {sys.outcome || sys.name}
                    </span>
                  </div>
                  <span className="text-[10px] text-[#00E5C0] font-medium ml-3 flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play size={8} weight="fill" /> {sys.cta}
                  </span>
                </button>
              ))}
            </div>

            {/* Already-active systems (subtle) */}
            {active.length > 0 && (
              <div className="mt-2 pt-2 border-t border-[rgba(255,255,255,0.03)]">
                {active.map(sys => (
                  <div key={sys.template_id} className="flex items-center gap-2 py-1" data-testid={`activation-active-${sys.template_id}`}>
                    <CheckCircle size={10} className="text-[#00E5C0]" weight="fill" />
                    <span className="text-[10px] text-[#5C6778]">{sys.name} — {sys.state_label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
